import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { playersService, playerTeamAssignmentsService, teamsService } from '../../../services/supabaseService';

const LeagueImportPlayersModal = ({ isOpen, onClose, leagueTeam, onRefresh }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile?.name?.endsWith('.csv')) {
      setError('Please select a valid CSV file');
      return;
    }

    setFile(selectedFile);
    setError('');
    setImportResult(null);
    parseCSV(selectedFile);
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.filter(line => line?.trim());

        if (lines?.length < 2) {
          setError('CSV file is empty or invalid');
          return;
        }

        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
        const requiredHeaders = ['name', 'role'];

        const missingHeaders = requiredHeaders?.filter(h => !headers?.includes(h));
        if (missingHeaders?.length > 0) {
          setError(`Missing required columns: ${missingHeaders?.join(', ')}`);
          return;
        }

        const data = [];
        for (let i = 1; i < lines?.length; i++) {
          const values = lines?.[i]?.split(',')?.map(v => v?.trim());
          const row = {};
          headers?.forEach((header, index) => {
            row[header] = values?.[index] || '';
          });

          if (row?.name && row?.role) {
            data?.push(row);
          }
        }

        if (data?.length === 0) {
          setError('No valid player data found in CSV');
          return;
        }

        setPreview(data?.slice(0, 5));
        setError('');
      } catch (err) {
        setError('Failed to parse CSV file. Please check the format.');
        console.error('CSV parsing error:', err);
      }
    };
    reader?.readAsText(file);
  };

  const handleImport = async () => {
    if (!file || preview?.length === 0) return;

    setIsProcessing(true);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.filter(line => line?.trim());
        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());

        const allRows = [];
        for (let i = 1; i < lines?.length; i++) {
          const values = lines?.[i]?.split(',')?.map(v => v?.trim());
          const row = {};
          headers?.forEach((header, index) => {
            row[header] = values?.[index] || '';
          });

          if (!row?.name || !row?.role) continue;

          const validRoles = ['batter', 'batsman', 'bowler', 'all-rounder', 'wicket-keeper'];
          const role = row?.role?.toLowerCase()?.replace(/\s+/g, '-');

          if (!validRoles?.includes(role)) {
            console.warn(`Skipping player ${row?.name}: Invalid role ${row?.role}`);
            continue;
          }

          const roleMap = {
            'batter': 'Batter',
            'batsman': 'Batter',
            'bowler': 'Bowler',
            'all-rounder': 'All-rounder',
            'wicket-keeper': 'Wicket Keeper'
          };

          allRows?.push({
            name: row?.name,
            role: roleMap?.[role],
            battingStyle: row?.batting_style || 'right-hand',
            bowlingStyle: row?.bowling_style || 'none',
            jerseyNumber: row?.jersey_number ? parseInt(row?.jersey_number) : null,
            teamName: row?.team_name || ''
          });
        }

        if (allRows?.length === 0) {
          setError('No valid players found. Please check role values.');
          setIsProcessing(false);
          return;
        }

        // Fetch all country teams to resolve team_name → teamId
        const allTeams = await teamsService?.getAll();
        const countryTeamMap = {};
        allTeams?.forEach(t => {
          if (t?.teamType === 'country') {
            countryTeamMap[t?.name?.toLowerCase()?.trim()] = t?.id;
          }
        });

        // Fetch all existing players to check if player already exists in any country team
        const allExistingPlayers = await playersService?.getAll();
        const existingPlayerMap = {};
        allExistingPlayers?.forEach(p => {
          existingPlayerMap[p?.name?.toLowerCase()?.trim()] = p;
        });

        // Fetch current active assignments for this league team to avoid duplicate assignments
        const activeAssignments = await playerTeamAssignmentsService?.getActiveByTeam(leagueTeam?.id);
        const assignedPlayerIds = new Set(activeAssignments?.map(a => a?.playerId));

        let assigned = 0;
        let created = 0;
        let alreadyInRoster = 0;
        const errors = [];

        for (const row of allRows) {
          const nameLower = row?.name?.toLowerCase()?.trim();
          let existingPlayer = existingPlayerMap?.[nameLower];

          // If player already in league roster, skip
          if (existingPlayer && assignedPlayerIds?.has(existingPlayer?.id)) {
            alreadyInRoster++;
            continue;
          }

          // If player doesn't exist anywhere, create them
          if (!existingPlayer) {
            try {
              // Resolve teamId from team_name column if provided
              const resolvedTeamId = row?.teamName
                ? (countryTeamMap?.[row?.teamName?.toLowerCase()?.trim()] || null)
                : null;

              const newPlayer = await playersService?.create({
                name: row?.name,
                teamId: resolvedTeamId,
                role: row?.role,
                battingStyle: row?.battingStyle,
                bowlingStyle: row?.bowlingStyle,
                jerseyNumber: row?.jerseyNumber
              });
              existingPlayer = newPlayer;
              existingPlayerMap[nameLower] = newPlayer;
              created++;
            } catch (err) {
              console.error(`Failed to create player ${row?.name}:`, err);
              errors?.push(`Failed to create: ${row?.name}`);
              continue;
            }
          }

          // Assign player to league roster
          try {
            await playerTeamAssignmentsService?.assign({
              playerId: existingPlayer?.id,
              teamId: leagueTeam?.id,
              effectiveFrom: new Date()?.toISOString()?.split('T')?.[0],
              effectiveUntil: null,
              transferReason: 'import',
              season: null
            });
            assignedPlayerIds?.add(existingPlayer?.id);
            assigned++;
          } catch (err) {
            console.error(`Failed to assign player ${row?.name}:`, err);
            errors?.push(`Failed to assign: ${row?.name}`);
          }
        }

        setImportResult({ assigned, created, alreadyInRoster, errors });
        onRefresh?.();
      } catch (err) {
        setError('Failed to import players. Please try again.');
        console.error('Import error:', err);
      } finally {
        setIsProcessing(false);
      }
    };

    reader?.readAsText(file);
  };

  const handleClose = () => {
    setFile(null);
    setError('');
    setPreview([]);
    setIsProcessing(false);
    setImportResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    const headers = 'name,role,batting_style,bowling_style,jersey_number,team_name';
    const example1 = 'Rohit Sharma,batter,right-hand,right-arm-medium,45,India';
    const example2 = 'Jasprit Bumrah,bowler,right-hand,right-arm-fast,93,India';
    const example3 = 'Hardik Pandya,all-rounder,right-hand,right-arm-fast,228,India';
    const csv = `${headers}\n${example1}\n${example2}\n${example3}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'league_roster_import_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1400] p-4">
      <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground">Import Players to League Roster</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Import players for <span className="font-medium text-foreground">{leagueTeam?.name}</span>. Existing players will be assigned directly; new players will be created and assigned.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors ml-4"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          {/* Info Banner */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800 dark:text-purple-200 space-y-1">
                <p className="font-medium">How this import works:</p>
                <ul className="list-disc list-inside space-y-1 text-purple-700 dark:text-purple-300">
                  <li>If a player already exists in any country team, they are assigned to this league roster directly</li>
                  <li>If a player doesn't exist yet, they are created and then assigned to this roster</li>
                  <li>Players already in this league roster are skipped</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Template Download */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Download CSV Template</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Columns: name, role, batting_style, bowling_style, jersey_number, team_name
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={downloadTemplate}
            >
              Template
            </Button>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Select CSV File</label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
              <Icon name="Upload" className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                {file ? file?.name : 'Click to select or drag and drop a CSV file'}
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="league-csv-upload"
              />
              <label
                htmlFor="league-csv-upload"
                className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Icon name="FolderOpen" size={16} />
                Browse File
              </label>
            </div>
            {error && (
              <p className="mt-2 text-sm text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={14} />
                {error}
              </p>
            )}
          </div>

          {/* Preview */}
          {preview?.length > 0 && !importResult && (
            <div>
              <p className="text-sm font-medium text-foreground mb-2">
                Preview (first {preview?.length} rows)
              </p>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-foreground">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-foreground">Role</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-foreground hidden sm:table-cell">Batting Style</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-foreground hidden sm:table-cell">Bowling Style</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {preview?.map((row, idx) => (
                      <tr key={idx} className="hover:bg-muted/30">
                        <td className="px-3 py-2 text-foreground">{row?.name}</td>
                        <td className="px-3 py-2 text-foreground">{row?.role}</td>
                        <td className="px-3 py-2 text-muted-foreground hidden sm:table-cell">{row?.batting_style || '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground hidden sm:table-cell">{row?.bowling_style || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-foreground">Import Complete</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700 dark:text-green-300">{importResult?.assigned}</p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">Assigned to Roster</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{importResult?.created}</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">New Players Created</p>
                </div>
                <div className="bg-muted border border-border rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-muted-foreground">{importResult?.alreadyInRoster}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Already in Roster</p>
                </div>
              </div>
              {importResult?.errors?.length > 0 && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                  <p className="text-sm font-medium text-destructive mb-1">Errors ({importResult?.errors?.length})</p>
                  <ul className="text-xs text-destructive space-y-0.5 max-h-24 overflow-y-auto">
                    {importResult?.errors?.map((err, idx) => (
                      <li key={idx}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <Button variant="outline" onClick={handleClose}>
              {importResult ? 'Close' : 'Cancel'}
            </Button>
            {!importResult && (
              <Button
                variant="default"
                iconName={isProcessing ? undefined : 'Upload'}
                iconPosition="left"
                onClick={handleImport}
                disabled={!file || preview?.length === 0 || isProcessing}
              >
                {isProcessing ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></span>
                    Importing...
                  </span>
                ) : (
                  'Import Players'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeagueImportPlayersModal;
